package ru.it_mozaika.sender.js.application.components.campaigns

import emotion.react.css
import io.ktor.client.call.*
import io.ktor.http.*
import js.core.jso
import kotlinx.browser.window
import mui.lab.TabContext
import mui.lab.TabList
import mui.lab.TabPanel
import mui.material.*
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.responsive
import mui.system.sx
import oldcomponents.NotyType
import oldcomponents.reactEChartsCore
import oldframes.campaigns.toLocalDateTime
import react.*
import react.dom.events.MouseEvent
import react.dom.html.ReactHTML.iframe
import react.router.useNavigate
import ru.it_mozaika.components.AvatarButton
import ru.it_mozaika.components.useSnackbar
import ru.it_mozaika.sender.common.models.EmailCampaignStatistics
import ru.it_mozaika.sender.common.models.EmailCampaignVariantStatistics
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.sender.js.application.context.BackdropContext
import ru.it_mozaika.utils.checkResponseError
import ru.it_mozaika.sender.js.application.context.CampaignProvider
import ru.it_mozaika.sender.js.application.context.useBackdrop
import ru.it_mozaika.sender.js.application.context.useCampaign
import ru.it_mozaika.sender.js.application.frames.MainCard
import web.cssom.*
import web.dom.document
import web.html.HTMLDivElement
import web.html.HTMLElement
import web.html.HTMLIFrameElement

val EmailCampaignStatisticsFrame = VFC {
    val backdrop = useBackdrop()
    val navigate = useNavigate()
    val showNotification = useSnackbar().enqueueSnackbar
    var activeStep by useState(0)
    var statistics by useState<EmailCampaignStatistics>()
    val campaignContext = useCampaign()
    val (showCampaignStatisticsDialog, setShowCampaignStatisticsDialog) = useState(false)
    useEffect(campaignContext.campaign.id) {
        if (campaignContext.campaign.id.isBlank())
            return@useEffect
        backdrop.show()
        Http.get("/campaigns/${campaignContext.campaign.id}/statistics") { response ->
            backdrop.hide()
            response.checkResponseError()?.let { error ->
                showNotification(error, jso { variant = AlertColor.error })
                return@get
            }
            statistics = response.body()
        }
    }
    campaignsStatisticsDownloadDialog{
        isOpen = showCampaignStatisticsDialog
        onClose = { setShowCampaignStatisticsDialog(false) }
        campaign = campaignContext.campaign.id
    }

    Card {
        sx {
            ":hover" {
                boxShadow = BoxShadow(0.px, 2.px, 14.px, 0.px, Color("rgb(32 40 45 / 8%)"))
            }
        }
        CardHeader {
            title = ReactNode(campaignContext.campaign.name)
            action = Stack.create {
                direction = responsive(StackDirection.row)
                spacing = responsive(1)
                sx {
                    alignItems = AlignItems.center
                    justifyContent = JustifyContent.flexEnd
                }
                Button {
                    variant = ButtonVariant.outlined
                    color = ButtonColor.primary
                    startIcon = mui.icons.material.ArrowBack.create()
                    onClick = { navigate("/campaigns") }
                    +"Назад"
                }
                Button {
                    variant = ButtonVariant.contained
                    color = ButtonColor.primary
                    startIcon = mui.icons.material.ContentCopy.create()
                    onClick = { navigate("/campaigns/new?id=${campaignContext.campaign.id}") }
                    +"Скопировать"
                }
                Button {
                    asDynamic().component = "a"
                    asDynamic().href = "/campaigns/${campaignContext.campaign.id}/preview"
                    asDynamic().target = "_blank"
                    variant = ButtonVariant.contained
                    color = ButtonColor.primary
                    startIcon = mui.icons.material.Visibility.create()
                    disabled = campaignContext.campaign.id.isBlank() || !campaignContext.isSaved
                    +"Предпросмотр"
                }
            }
        }
        Divider()
        val steps = listOf("Основная информация", "Статистика", "Карта кликов")
        Stepper {
            sx {
                margin = 24.px
            }
            this.activeStep = activeStep
            nonLinear = true
            steps.forEachIndexed { idx, title ->
                Step {
                    key = "step$idx"
                    completed = activeStep >= idx
                    StepButton {
                        asDynamic().onClick = { _: MouseEvent<HTMLDivElement, *> ->
                            activeStep = idx
                        }
                        +title
                    }
                }
            }
        }
    }

    when (activeStep) {
        0 -> SettingsFragment { disabled = true }
        1 -> StatisticsFragment { statistics?.let { this.statistics = it } }
        2 -> ClickMapFragment { statistics?.let { this.statistics = it } }
    }
}

private val statisticsHeaders = listOf("Номер варианта", "Отправлено", "Доставлено", "Прочитано", "Прочитано (уникальные)", "Клики", "Клики (уникальные)", "Ошибки", "Отписались", "Спам")
external interface StatisticsFragmentProps : Props {
    var statistics: EmailCampaignStatistics
}
private val StatisticsFragment = FC<StatisticsFragmentProps> { props ->
    val backdrop = useContext(BackdropContext)
    val campaignContext = useCampaign()
    var totalStats by useState(EmailCampaignVariantStatistics())
    useEffect(props.statistics) {
        totalStats = EmailCampaignVariantStatistics(
            props.statistics.variants.sumOf { it.send },
            0,
            props.statistics.variants.sumOf { it.delivery },
            props.statistics.variants.sumOf { it.errors },
            props.statistics.variants.sumOf { it.open },
            props.statistics.variants.sumOf { it.openUnique },
            props.statistics.variants.sumOf { it.click },
            props.statistics.variants.sumOf { it.clickUnique },
            props.statistics.variants.sumOf { it.unsubscribe },
            props.statistics.variants.sumOf { it.spam },
        )
    }
    if (undefined == props.statistics)
        return@FC

    TabFragment {
        Grid {
            item = true
            asDynamic().xs = 12

            TableContainer {
                component = Card

                Table {
                    TableHead {
                        TableRow {
                            statisticsHeaders.forEach {
                                TableCell {
                                    when(it){
                                        "Ошибки" -> Tooltip {
                                            title = ReactNode("Скачать данные по ошибкам")
                                            Button {
                                                +it
                                                variant = ButtonVariant.text
                                                startIcon = mui.icons.material.DownloadSharp.create()
                                                onClick = {
                                                    backdrop.show()
                                                    val url =
                                                        URLBuilder("${window.location.protocol}${window.location.host}/api/v1/emails/excel")
                                                    url.parameters.appendAll("status", listOf("6"))
                                                    url.parameters.append("campaign", campaignContext.campaign.id)
                                                    val element = kotlinx.browser.document.createElement("a")
                                                    element.setAttribute("href", url.toString())
                                                    kotlinx.browser.document.body?.appendChild(element)
                                                    @Suppress("UnsafeCastFromDynamic")
                                                    element.asDynamic().click()
                                                    backdrop.hide()
                                                }
                                                color = ButtonColor.secondary
                                            }
                                        }
                                        "Клики" -> Tooltip {
                                            title = ReactNode("Скачать данные по кликам")
                                            Button {
                                                +it
                                                variant = ButtonVariant.text
                                                startIcon = mui.icons.material.DownloadSharp.create()
                                                onClick = {
                                                    backdrop.show()
                                                    val url =
                                                        URLBuilder("${window.location.protocol}${window.location.host}/api/v1/emails/excel")
                                                    url.parameters.appendAll("status", listOf("8"))
                                                    url.parameters.append("campaign", campaignContext.campaign.id)
                                                    val element = kotlinx.browser.document.createElement("a")
                                                    element.setAttribute("href", url.toString())
                                                    kotlinx.browser.document.body?.appendChild(element)
                                                    @Suppress("UnsafeCastFromDynamic")
                                                    element.asDynamic().click()
                                                    backdrop.hide()
                                                }
                                                color = ButtonColor.secondary
                                            }
                                        }
                                        else -> + it
                                    }
                                }
                            }
                        }
                    }
                    TableBody {

                        val variantsSize = props.statistics.variants.size
                        if (variantsSize > 1) {
                            props.statistics.variants.forEachIndexed { index, variantStats ->
                                TableRow {
                                    key = "variant-${index}"
                                    sx {
                                        "&:last-child td, &:last-child th" {
                                            border = Border(0.px, web.cssom.LineStyle.solid)
                                        }
                                    }
                                    TableCell {
                                        variant = TableCellVariant.head
                                        scope = "row"
                                        +"Вариант ${index + 1}"
                                    }

                                    StatisticsTableCells {
                                        this.variantStats = variantStats
                                        this.totalStats = totalStats
                                        variantsCount = variantsSize
                                    }
                                }
                            }
                        }
                        TableRow {
                            key = "variant-total"
                            sx {
                                "&:last-child td, &:last-child th" {
                                    border = Border(0.px, web.cssom.LineStyle.solid)
                                }
                            }
                            TableCell {
                                variant = TableCellVariant.head
                                scope = "row"
                                +"Всего"
                            }
                            StatisticsTableCells {
                                this.variantStats = totalStats
                                this.totalStats = totalStats
                            }
                        }
                    }
                }
            }
        }
        Grid {
            item = true
            asDynamic().xs = 12
            MainCard {
                reactEChartsCore {
                    option = jso {
                        tooltip = jso { trigger = "axis" }
                        legend = jso {
                            this.data = arrayOf("Отправлено", "Доставлено", "Открыто", "Клики", "Ошибки")
                        }
                        xAxis = jso {
                            type = "category"
                            boundaryGap = false
                            // TODO: Add select of variant
                            this.data = props.statistics.daysStats[0]?.keys?.sortedBy {
                                it.toLocalDateTime()
                            }?.toTypedArray() ?: arrayOf<String>()
                        }
                        yAxis = jso {
                            type = "value"
                        }
                        series = props.statistics.daysStats[0]?.let { stats ->

                            val values = stats.entries.sortedBy {
                                it.key.toLocalDateTime()
                            }.map { it.value }
                            arrayOf<dynamic>(
                                jso {
                                    this.data = values.map { it.send }.toTypedArray()
                                    name = "Отправлено"
                                    type = "line"
                                    smooth = true
                                },
                                jso {
                                    this.data = values.map { it.delivery }.toTypedArray()
                                    name = "Доставлено"
                                    type = "line"
                                    smooth = true
                                },
                                jso {
                                    this.data = values.map { it.open }.toTypedArray()
                                    name = "Открыто"
                                    type = "line"
                                    smooth = true
                                },
                                jso {
                                    this.data = values.map { it.click }.toTypedArray()
                                    name = "Клики"
                                    type = "line"
                                    smooth = true
                                },
                                jso {
                                    this.data = values.map { it.errors }.toTypedArray()
                                    name = "Ошибки"
                                    type = "line"
                                    smooth = true
                                },
                            )
                        } ?: arrayOf<dynamic>()
                    }
                }
            }
        }
    }
}

external interface StatisticsTableCellsProps : Props {
    var variantStats: EmailCampaignVariantStatistics
    var totalStats: EmailCampaignVariantStatistics
    var variantsCount: Int
}
private val StatisticsTableCells = FC<StatisticsTableCellsProps> { props ->
    val variantStats = props.variantStats
    val totalStats = props.totalStats
    listOf(
        Triple(variantStats.send, variantStats.send, totalStats.send),
        Triple(variantStats.delivery, variantStats.send, totalStats.send),
        Triple(variantStats.open, variantStats.delivery, totalStats.delivery),
        Triple(variantStats.openUnique, variantStats.delivery, totalStats.delivery),
        Triple(variantStats.click, variantStats.open, totalStats.open),
        Triple(variantStats.clickUnique, variantStats.open, totalStats.open),
        Triple(variantStats.errors, variantStats.send, totalStats.send),
        Triple(variantStats.unsubscribe, variantStats.send, totalStats.send),
        Triple(variantStats.spam, variantStats.send, totalStats.send),
    ).forEach { (c, vd, td) ->
        StatisticsTableCell {
            count = c
            variantDivider = vd
            totalDivider = td
            if (undefined != props.variantsCount) {
                variantsCount = props.variantsCount
            }
        }
    }
}
external interface StatisticsTableCellProps : Props {
    var count: Int
    var variantDivider: Int
    var totalDivider: Int
    var variantsCount: Int
}
private val StatisticsTableCell = FC<StatisticsTableCellProps> { props ->
    TableCell {
        var percent = "${props.count * 100 / props.variantDivider}%"
        if (undefined != props.variantsCount && props.variantsCount > 1)
            percent = "$percent / ${props.count * 100 / props.totalDivider}%"
        +"${props.count} ( $percent )"
    }
}

private val ClickMapFragment = FC<StatisticsFragmentProps> { props ->
    val campaignContext = useCampaign()
    val iframeRef = useRef<HTMLIFrameElement>()
    var bodyElements by useState(mapOf<Int, HTMLElement>())
    var clicksInitialized by useState(listOf<Int>())
    var selectedVariant by useState(0)

    useEffect(bodyElements, props.statistics, selectedVariant) {
        if (undefined == props.statistics && bodyElements.isNotEmpty()) {
            return@useEffect
        }
        bodyElements[selectedVariant]?.let { body ->
            clicksInitialized = clicksInitialized.plus(0)
            body.ownerDocument.createElement("div").also { clicksDiv ->
                clicksDiv.setAttribute(
                    "style",
                    "position: absolute;width: 100%; height: 100%; z-index: 10000; left: 0; top: 0;"
                )
                body.getElementsByTagName("a").let { links ->
//                    val stats =
//                        props.statistics.clicks.firstOrNull()?.clicks?.associate { it._id to it.count } ?: mapOf()
//                    val max = props.statistics.clicks.firstOrNull()?.clicks?.maxOf { it.count } ?: 0
                    val stats = props.statistics.clicks.find{ it._id == selectedVariant }?.clicks?.associate { it._id to it.count } ?: mapOf()
                    val max = props.statistics.clicks.find{ it._id == selectedVariant }?.clicks?.maxOf { it.count } ?: 0
                    for (i in 0 until links.length) {
                        links[i].let { link ->
                            stats[i]?.let { count ->
                                body.ownerDocument.createElement("div").also { statsDiv ->
                                    val size = 30 + (30 * (count.toFloat() / max)).toInt()
                                    link.getBoundingClientRect().let { rect ->
                                        val left = (rect.left + rect.width / 2.0f).toInt() - size / 2
                                        val top = (rect.top + rect.height / 2.0f).toInt() - size / 2
                                        statsDiv.setAttribute(
                                            "style",
                                            "position: absolute;left: ${left}px;top:${top}px;border-radius: 50%; background-color: #5d5386f0; color: white; font-size: 1.2rem; width: ${size}px; height: ${size}px; text-align: center; line-height: ${size}px;"
                                        )
                                    }
                                    statsDiv.textContent = count.toString()
                                    clicksDiv.appendChild(statsDiv)
                                }
                            }
                        }
                    }
                }
                body.appendChild(clicksDiv)
            }
        }
    }

    useEffect(selectedVariant) {
        (0 until campaignContext.campaign.variants.size).forEach { index ->
//            if (!bodyElements.containsKey(index)) {
                println("!bodyElements.containsKey($index)")
                (document.getElementById("preview$index") as? HTMLIFrameElement)?.let { frame ->
                    if ((frame.contentDocument?.body?.childElementCount ?: 0) > 0) {
                        bodyElements = bodyElements.plus(index to frame.contentDocument?.body!!)
                    } else {
                        frame.onload = {
                            bodyElements = bodyElements.plus(index to frame.contentDocument?.body!!)
                            it
                        }
                    }
                }
//            }
        }
    }
//    useEffect(selectedVariant) {
//        if (!bodyElements.containsKey(selectedVariant)) {
//            (document.getElementById("preview$selectedVariant") as? HTMLIFrameElement)?.let { frame ->
//                if ((frame.contentDocument?.body?.childElementCount ?: 0) > 0) {
//                    bodyElements = bodyElements.plus(selectedVariant to frame.contentDocument?.body!!)
//                } else {
//                    frame.onload = {
//                        bodyElements = bodyElements.plus(selectedVariant to frame.contentDocument?.body!!)
//                        it
//                    }
//                }
//            }
//        }
//
//    }

    TabFragment {
        Grid {
            item = true
            asDynamic().xs = 12
            MainCard {
                TabContext {
                    value = selectedVariant.toString()
                    TabList {
                        campaignContext.campaign.variants.forEachIndexed { index, _ ->
                            Tab {
                                sx {
                                    flexGrow = number(1.0)
                                    maxWidth = None.none
                                }
                                onClick = { selectedVariant = index }
                                label = ReactNode("Вариант ${index + 1}")
                                value = index.toString()
                            }
                        }
                    }
                    campaignContext.campaign.variants.forEachIndexed { index, _ ->
                        TabPanel {
                            value = index.toString()
                            iframe {
                                src = "/api/v1/campaigns/${campaignContext.campaign.id}/preview?variant=$index"
                                ref = iframeRef
//                                    id = "preview0"
                                id = "preview$index"
                                css {
                                    width = 100.pct
                                    height = 100.vh - 350.px
                                    minHeight = 400.px
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}